import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API_BASE_URL } from "../../../config/api";
import { FaFilePdf, FaFolder, FaGraduationCap } from "react-icons/fa";
import { InboxMetricCard } from "../../../inbox/components/molecules/InboxMetricCard";

export const CandidateCard = ({ candidateId }) => {
  const [metrics, setMetrics] = useState({ cv: 0, academic: 0, certificates: 0 });

  const token = useMemo(() => localStorage.getItem("token"), []);
  const axiosCfg = useMemo(
    () => (token ? { headers: { Authorization: `Bearer ${token}` } } : {}),
    [token]
  );

  useEffect(() => {
    let cancelled = false;
    if (!candidateId || !token) return;

    (async () => {
      try {
        const res = await axios.get(
          `${API_BASE_URL}/api/candidates/${candidateId}/docs/summary`,
          axiosCfg
        );
        const data = res?.data || {};
        if (!cancelled) {
          setMetrics({
            cv: data.cvExists ? 1 : 0,
            academic: Number(data.academicCount || 0),
            certificates: Number(data.certificatesCount || 0),
          });
        }
      } catch (err) {
        // Fallbacks if /docs/summary doesn't exist
        try {
          await axios.head(`${API_BASE_URL}/api/candidates/${candidateId}/doc/cv`, axiosCfg);
          if (!cancelled) setMetrics((m) => ({ ...m, cv: 1 }));
        } catch (_) {
          if (!cancelled) setMetrics((m) => ({ ...m, cv: 0 }));
        }
        try {
          const academic = await axios.get(
            `${API_BASE_URL}/api/candidates/${candidateId}/doc/creds/list`,
            axiosCfg
          );
          const list = Array.isArray(academic?.data) ? academic.data : [];
          if (!cancelled) setMetrics((m) => ({ ...m, academic: list.length || 0 }));
        } catch (_) {
          if (!cancelled) setMetrics((m) => ({ ...m, academic: 0 }));
        }
        try {
          const certs = await axios.get(
            `${API_BASE_URL}/api/candidates/${candidateId}/doc/certificates/list`,
            axiosCfg
          );
          const list = Array.isArray(certs?.data) ? certs.data : [];
          if (!cancelled) setMetrics((m) => ({ ...m, certificates: list.length || 0 }));
        } catch (_) {
          if (!cancelled) setMetrics((m) => ({ ...m, certificates: 0 }));
        }
      }
    })();

    return () => { cancelled = true; };
  }, [candidateId, token, axiosCfg]);

  const downloadBlob = async (url, fileName) => {
    try {
      const res = await axios.get(url, { ...axiosCfg, responseType: "blob" });
      const blob = new Blob([res.data], { type: res.headers["content-type"] || "application/octet-stream" });
      const link = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = link;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(link);
    } catch (err) {
      console.error(`Download failed for ${url}:`, err?.response?.data || err?.message);
      alert("File not available or an error occurred.");
    }
  };

  const handleMetricClick = async (title) => {
    if (!candidateId) return;

    if (title === "CV") {
      await downloadBlob(`${API_BASE_URL}/api/candidates/${candidateId}/doc/cv`, `candidate_${candidateId}_cv.pdf`);
    } else if (title === "Academic") {
      await downloadBlob(`${API_BASE_URL}/api/candidates/${candidateId}/doc/creds`, `candidate_${candidateId}_academic.pdf`);
    } else if (title === "Certificates") {
      await downloadBlob(`${API_BASE_URL}/api/candidates/${candidateId}/doc/certificates`, `candidate_${candidateId}_certificates.pdf`);
    }
  };

  return (
    <div className="flex justify-start w-full">
      <div className="grid grid-cols-3 gap-4">
        <button onClick={() => handleMetricClick("CV")} className="text-left cursor-pointer focus:outline-none">
          <InboxMetricCard title="CV" value={metrics.cv} Icon={FaFolder} />
        </button>

        <button onClick={() => handleMetricClick("Academic")} className="text-left cursor-pointer focus:outline-none">
          <InboxMetricCard title="Academic" value={metrics.academic} Icon={FaGraduationCap} />
        </button>

        <button onClick={() => handleMetricClick("Certificates")} className="text-left cursor-pointer focus:outline-none">
          <InboxMetricCard title="Certificates" value={metrics.certificates} Icon={FaFilePdf} />
        </button>
      </div>

      <style>{`
        :where(button, a, input, select, textarea):focus {
          outline: 2px solid #FFCC00;
          box-shadow: 0 0 0 3px rgba(255,204,0,.35);
          border-color: #FFCC00 !important;
        }
      `}</style>
    </div>
  );
};
